<script setup lang="ts">
/* A <dl> of field → value for the detail pages.

   Rows are data rather than slots. The one thing a value needed beyond text
   was to be a link, and a link is a URL — expressing that as `href` keeps the
   whole page describable as an array, and avoids per-label scoped slots,
   which vue-tsc cannot type when the names come from data. */
import type { IconName } from '@/components/icons'
import Badge from './Badge.vue'

export interface DetailRow {
  label: string
  /** Rendered as-is when no slot content is supplied. */
  value?: string
  /**
   * Rendered as a row of badges instead of `value`. For a set — amenities,
   * roles — where the members are individually meaningful and a comma-joined
   * string reads as one long sentence.
   */
  tags?: string[]
  /** Renders the value muted, for "not set". */
  empty?: boolean
  /** Monospace + tabular figures, for ids and coordinates. */
  mono?: boolean
  /** Renders the value as a link — a mailto:, or anything else. */
  href?: string
  /**
   * Renders the value as an in-app link, routed rather than followed. Use
   * this for anything inside the panel; `href` full-reloads the SPA, which is
   * right for mailto: and wrong for /companies/12.
   */
  to?: string
  icon?: IconName
}

defineProps<{ rows: DetailRow[] }>()

/* A mailto: hands off to the mail client and leaves the page alone. An http(s)
   link navigates away from the panel, so it opens in a tab of its own — with
   `noopener`, without which the opened page can reach back through
   window.opener. */
const isExternal = (href: string): boolean => /^https?:\/\//i.test(href)
</script>

<template>
  <dl class="list">
    <div v-for="row in rows" :key="row.label" class="row">
      <dt class="term">{{ row.label }}</dt>
      <dd class="value" :class="{ empty: row.empty, mono: row.mono }">
        <template v-if="row.empty">—</template>
        <span v-else-if="row.tags" class="tags">
          <Badge v-for="t in row.tags" :key="t">{{ t }}</Badge>
        </span>
        <RouterLink v-else-if="row.to" :to="row.to">{{ row.value }}</RouterLink>
        <a
          v-else-if="row.href"
          :href="row.href"
          :target="isExternal(row.href) ? '_blank' : undefined"
          :rel="isExternal(row.href) ? 'noopener noreferrer' : undefined"
          >{{ row.value }}</a
        >
        <template v-else>{{ row.value }}</template>
      </dd>
    </div>
  </dl>
</template>

<style scoped>
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3xs);
}

.list {
  display: grid;
  gap: 0;
}

.row {
  display: grid;
  gap: var(--space-3xs) var(--space-md);
  padding: var(--space-xs) 0;
  border-bottom: var(--border-width) solid var(--color-border);
}

.row:last-child {
  border-bottom: 0;
}

/* Two columns once there is room for a label column that does not squeeze
   the value; stacked below that. */
@media (min-width: 34rem) {
  .row {
    grid-template-columns: 12rem 1fr;
    align-items: baseline;
  }
}

.term {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
}

.value {
  font-size: var(--text-sm);
  color: var(--color-text);
  overflow-wrap: anywhere;
}

.empty {
  color: var(--color-text-faint);
}

.mono {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
}
</style>
